import { EventEmitter, FunctionalComponent, VNode, h } from '@stencil/core';
import { Icon, SVGIcon, getIconObject } from 'src/components/core/interfaces/Icon';
import {
  TabBlurEvent,
  TabBlurHandler,
  TabCloseEvent,
  TabCloseHandler,
  TabFocusEvent,
  TabFocusHandler,
  TabSelectEvent,
  TabSelectHandler,
} from '../../ads-horizontal-tabs.types';
import { generateSVGImage } from 'utils/imageHelper';
import { height, svgPathData, width } from '@fortawesome/pro-regular-svg-icons/faTimesCircle';
import { isNullOrUndefined } from 'utils/collection';

interface AdsHorizontalTabsTabProps {
  tabsId: string;
  id: string;
  isActive: boolean;
  icon?: Icon | SVGIcon;
  label: string;
  dismissible?: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: EventEmitter<TabSelectEvent>;
  selectHandler?: TabSelectHandler;
  onBlur: EventEmitter<TabBlurEvent>;
  blurHandler?: TabBlurHandler;
  onClose: EventEmitter<TabCloseEvent>;
  closeHandler?: TabCloseHandler;
  onFocus: EventEmitter<TabFocusEvent>;
  focusHandler?: TabFocusHandler;
  attributes?: any;
  availablePanelIds?: Set<string>;
}

export const AdsHorizontalTabsTab: FunctionalComponent<AdsHorizontalTabsTabProps> = ({
  tabsId,
  id,
  isActive,
  icon,
  label,
  dismissible,
  isFirst,
  isLast,
  onSelect,
  onBlur,
  onClose,
  onFocus,
  selectHandler,
  blurHandler,
  closeHandler,
  focusHandler,
  attributes,
  availablePanelIds,
}) => {
  const blur = (evt: FocusEvent): void => {
    onBlur.emit({ evt: evt, id: id });
    if (!isNullOrUndefined(blurHandler)) {
      blurHandler({ evt: evt, id: id });
    }
  };
  const close = (evt: MouseEvent): void => {
    evt.stopImmediatePropagation();
    onClose.emit({ evt: evt, id: id });
    if (!isNullOrUndefined(closeHandler)) {
      closeHandler({ evt: evt, id: id });
    }
  };
  const focus = (evt: FocusEvent): void => {
    onFocus.emit({ evt: evt, id: id });
    if (!isNullOrUndefined(focusHandler)) {
      focusHandler({ evt: evt, id: id });
    }
  };
  const click = (evt: MouseEvent): void => {
    onSelect.emit({ evt: evt, id: id });
    if (!isNullOrUndefined(selectHandler)) {
      selectHandler({ evt: evt, id: id });
    }
  };
  const keyUp = (evt: KeyboardEvent): void => {
    if (evt.code === 'Enter' || evt.code === 'Space' || evt.key === 'Enter' || evt.key === 'Spacebar') {
      evt.preventDefault();
      onSelect.emit({ evt: evt, id: id });
      if (!isNullOrUndefined(selectHandler)) {
        selectHandler({ evt: evt, id: id });
      }
    }
  };

  // Only set aria-controls when panel ID is valid and the panel actually exists in the DOM
  // This ensures the attribute references an existing element
  const panelId = `tabs-${tabsId}-panel-${id}`;
  const buttonAttrs: Record<string, string | null> = {
    role: 'tab',
    'aria-label': label,
    'aria-selected': isActive ? 'true' : 'false',
    'data-track': 'true',
    'data-track-value': label,
  };

  // Only set aria-controls when panel ID is valid and the panel actually exists in the DOM
  // This ensures the attribute references an existing element
  if (panelId && panelId.trim().length > 0 && (!availablePanelIds || availablePanelIds.has(panelId))) {
    buttonAttrs['aria-controls'] = panelId;
  }

  // Update the button attributes if additional attributes are provided
  const finalButtonAttrs = typeof attributes === 'object' ? { ...buttonAttrs, ...attributes } : buttonAttrs;

  const buttonClasses = ['c-horizontal-tabs__tab__btn'];
  if (isActive) {
    buttonClasses.push('is-active');
  }
  if (isFirst) {
    buttonClasses.push('is-first');
  }
  if (isLast) {
    buttonClasses.push('is-last');
  }

  const $closeButton = dismissible
    ? ((
        <button class={'c-horizontal-tabs__tab__close'} onClick={close} tabIndex={0} aria-label={`Close ${label} tab`}>
          {generateSVGImage({ data: svgPathData, height, width })}
        </button>
      ) as VNode)
    : null;

  const iconObj = getIconObject(icon as Icon);
  const classIcon = iconObj?.active;
  const $icon = classIcon ? <span class={`${classIcon} c-horizontal-tabs__tab__btn__icon`}></span> : null;

  return (
    <div class={`c-horizontal-tabs__tab`} data-active={isActive} data-slot={id}>
      <button
        id={`tabs-${tabsId}-tab-${id}`}
        class={buttonClasses.join(' ')}
        onClick={click}
        onBlur={blur}
        onFocus={focus}
        onKeyUp={keyUp}
        {...finalButtonAttrs}
        tabIndex={0}
      >
        <div>
          <span>
            {$icon}
            <span>{label}</span>
          </span>
        </div>
      </button>
      {$closeButton}
    </div>
  ) as VNode;
};
